import { useParams, useNavigate } from "react-router-dom";
import { useOrders } from "@/hooks/useOrders";
import { useBids } from "@/hooks/useBids";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, MapPin, Calendar, Truck, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function OrderBids() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { orders, isLoading: ordersLoading } = useOrders();
  const { bids, isLoading: bidsLoading, acceptBid, declineBid, isAccepting, isDeclining } = useBids(orderId);

  const order = orders?.find((o) => o.id === orderId);

  const handleAcceptBid = async (bidId: string) => {
    try {
      await acceptBid(bidId);
    } catch (error) {
      console.error("Error accepting bid:", error);
    }
  };

  const handleDeclineBid = async (bidId: string) => {
    try {
      await declineBid(bidId);
    } catch (error) {
      console.error("Error declining bid:", error);
    }
  };

  if (ordersLoading || bidsLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-[#5A6B7A]">Chargement...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-[#5A6B7A]">Commande non trouvée</div>
      </div>
    );
  }

  const hasAcceptedBid = bids?.some((bid) => bid.is_accepted);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/shipper/dashboard")}
          className="mb-6 text-[#0880AE] hover:bg-[#EBF4F8]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour au tableau de bord
        </Button>

        <h1 className="text-3xl md:text-4xl font-semibold text-[#37699E] font-['Lexend_Deca'] mb-8">
          Offres reçues - {order.order_number}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Details - Left Column */}
          <Card className="lg:col-span-1 bg-white rounded-xl shadow-md border border-[#DBE2EA]">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-[#37699E] font-['Lexend_Deca']">
                Détails de la commande
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2">
                <Package className="text-[#0880AE] mt-1 flex-shrink-0" size={18} />
                <div>
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">
                    Marchandise
                  </p>
                  <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    {order.cargo_title || order.cargo_description}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <MapPin className="text-[#0880AE] mt-1 flex-shrink-0" size={18} />
                <div>
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">
                    Itinéraire
                  </p>
                  <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    {order.pickup_city} → {order.delivery_city}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Calendar className="text-[#0880AE] mt-1 flex-shrink-0" size={18} />
                <div>
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">
                    Dates
                  </p>
                  <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    Enlèvement: {format(new Date(order.pickup_date), "dd/MM/yyyy", { locale: fr })}
                  </p>
                  <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    Livraison: {format(new Date(order.delivery_date), "dd/MM/yyyy", { locale: fr })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Truck className="text-[#0880AE] mt-1 flex-shrink-0" size={18} />
                <div>
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">
                    Type de remorque
                  </p>
                  <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    {order.trailer_type}
                  </p>
                </div>
              </div>

              {order.proposed_price && (
                <div className="pt-4 border-t border-[#DBE2EA]">
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium mb-1">
                    Prix proposé
                  </p>
                  <p className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                    {order.proposed_price} €
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bids List - Right Column */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-medium text-[#37699E] font-['Lexend_Deca']">
                Offres reçues ({bids?.filter((bid) => !bid.declined_at).length || 0})
              </h2>
            </div>

            {!bids || bids.length === 0 ? (
              <Card className="bg-white rounded-xl shadow-md border border-[#DBE2EA]">
                <CardContent className="py-12 text-center">
                  <p className="text-[#5A6B7A] font-['Funnel_Sans']">
                    Aucune offre reçue pour le moment
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bids
                  .filter((bid) => !bid.declined_at) // Filtrer les offres refusées
                  .map((bid) => (
                  <Card
                    key={bid.id}
                    className={`bg-white rounded-xl shadow-md border transition-all duration-200 ${
                      bid.is_accepted
                        ? "border-[#14A38B] ring-2 ring-[#14A38B]/20"
                        : bid.declined_at
                        ? "border-[#FF7171] ring-2 ring-[#FF7171]/20 opacity-60"
                        : "border-[#DBE2EA] hover:border-[#0880AE] hover:shadow-lg"
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-[#37699E] font-['Lexend_Deca']">
                              {bid.transporter_company?.name || "Transporteur"}
                            </h3>
                            {bid.is_accepted && (
                              <Badge className="bg-[#14A38B] text-white">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Acceptée
                              </Badge>
                            )}
                            {bid.declined_at && (
                              <Badge className="bg-[#FF7171] text-white">
                                Refusée
                              </Badge>
                            )}
                          </div>

                          <div className="space-y-1">
                            <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">
                              Contact
                            </p>
                            <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                              {bid.transporter_company?.email}
                            </p>
                            {bid.transporter_company?.phone && (
                              <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                                {bid.transporter_company.phone}
                              </p>
                            )}
                          </div>

                          <div className="pt-3 border-t border-[#DBE2EA]">
                            <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium mb-1">
                              Montant proposé
                            </p>
                            <p className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                              {bid.amount} €
                            </p>
                          </div>

                          {bid.message && (
                            <div className="pt-3 border-t border-[#DBE2EA]">
                              <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium mb-2">
                                Message
                              </p>
                              <p className="text-sm text-[#2D3748] font-['Funnel_Sans'] whitespace-pre-wrap">
                                {bid.message}
                              </p>
                            </div>
                          )}

                          <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                            Reçue le {format(new Date(bid.created_at), "dd/MM/yyyy 'à' HH:mm", { locale: fr })}
                          </p>
                        </div>

                        {!bid.is_accepted && !bid.declined_at && !hasAcceptedBid && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleAcceptBid(bid.id)}
                              disabled={isAccepting || isDeclining}
                              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                            >
                              {isAccepting ? "Acceptation..." : "Accepter"}
                            </Button>
                            <Button
                              onClick={() => handleDeclineBid(bid.id)}
                              disabled={isAccepting || isDeclining}
                              variant="outline"
                              className="border-2 border-[#FF7171] text-[#FF7171] hover:bg-[#FF7171]/10 font-semibold px-6 py-3 rounded-lg transition-all duration-200"
                            >
                              {isDeclining ? "Refus..." : "Refuser"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
